import React, { FC } from "react";
import { AppTheme } from "./App";

interface PolarCoordinates {
  x: number;
  y: number;
}

interface RingProps {
  theme: AppTheme;
  strings: string[];
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
}

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): PolarCoordinates => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeSegment = (
  x: number,
  y: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number
): string => {
  const outerStart = polarToCartesian(x, y, outerRadius, endAngle);
  const outerEnd = polarToCartesian(x, y, outerRadius, startAngle);

  const innerStart = polarToCartesian(x, y, innerRadius, endAngle);
  const innerEnd = polarToCartesian(x, y, innerRadius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  const d = [
    "M",
    outerStart.x,
    outerStart.y,
    "A",
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    0,
    outerEnd.x,
    outerEnd.y,
    "L",
    innerEnd.x,
    innerEnd.y,
    "A",
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    1,
    innerStart.x,
    innerStart.y,
    "Z",
  ].join(" ");

  return d;
};

const Ring: FC<RingProps> = ({
  theme,
  strings,
  cx,
  cy,
  innerRadius,
  outerRadius,
}) => {
  const segmentAngle = 360 / strings.length;

  return (
    <g>
      {strings.map((string, index) => {
        const startAngle = index * segmentAngle;
        const endAngle = (index + 1) * segmentAngle;
        const middleAngle = (startAngle + endAngle) / 2;

        const labelPosition = polarToCartesian(
          cx,
          cy,
          (outerRadius + innerRadius) / 2,
          middleAngle
        );

        return (
          <g key={index}>
            {/* Define gradients */}
            <defs>
              <linearGradient id={`grad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                {/* Conditional stops based on theme */} 
                {theme === 'bw' ? (
                  <>
                    <stop offset="0%" style={{stopColor: '#fbf5fe', stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: '#f1e6fa', stopOpacity: 1}} />
                  </>
                ) : theme === 'fikahjulet' ? (
                  <>
                    <stop offset="0%" style={{stopColor: '#7bfabd', stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: '#6ff6b7', stopOpacity: 1}} />
                  </>
                ) : ( /* Tomtbola */
                  <>
                    <stop offset="0%" style={{stopColor: '#f0f0f0', stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: '#e0e0e0', stopOpacity: 1}} />
                  </>
                )}
              </linearGradient>
            </defs>
            {/* Segment */}
            <path
              d={describeSegment(
                cx,
                cy,
                innerRadius,
                outerRadius,
                startAngle,
                endAngle
              )}
              id="curve"
              fill={`url(#grad-${index})`} 
              stroke={
                theme === 'bw' 
                  ? '#dcdcdc' /* Lighter grey stroke for BW */
                  : theme === 'fikahjulet' 
                    ? '#100a1c' /* Dark stroke for Fikahjulet */
                    : '#b0b0b0' /* Grey stroke for Tomtbola */
              }
              strokeWidth="1.5"
              filter="drop-shadow(0px 2px 3px rgba(0,0,0,0.05))" /* Even softer shadow */
            />

            {/* Label */}
            <text
              x={labelPosition.x}
              y={labelPosition.y}
              fill={theme === 'bw' ? '#000000' : theme === 'fikahjulet' ? '#180f26' : '#333'} /* Ensure pure black text for BW */
              fontSize="22" 
              fontFamily="'Geist Sans', sans-serif"
              fontWeight={theme === 'bw' ? 600 : 600} /* Adjusted font weight for BW theme */
              textAnchor="middle"
              dy=".3em" // vertically center
              transform={`rotate(${middleAngle} ${labelPosition.x} ${labelPosition.y})`} // Rotate the text
            >
              {string}
            </text>
          </g>
        );
      })}
    </g>
  );
};

export default Ring;
